import { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";

export default function AdminAnnouncements() {
    const [items, setItems] = useState([]);
    const [title, setTitle] = useState("");
    const [body, setBody] = useState("");
    const [pinned, setPinned] = useState(false);
    const [pushTelegram, setPushTelegram] = useState(true);
    const [pushWeb, setPushWeb] = useState(false);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [msg, setMsg] = useState(null);
    const [error, setError] = useState(null);

    const load = async () => {
        try {
            const r = await api.get("/announcements", { params: { limit: 50 } });
            setItems(r.data || []);
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setLoading(false); }
    };
    useEffect(() => { load(); }, []);

    const flash = (m) => { setMsg(m); setError(null); setTimeout(() => setMsg(null), 4000); };

    const publish = async () => {
        if (!title.trim() || !body.trim()) { setError("Titolo e testo sono obbligatori."); return; }
        setSaving(true); setError(null);
        try {
            const r = await api.post("/admin/announcements", {
                title, body, pinned, push_telegram: pushTelegram, push_web: pushWeb,
            });
            const tg = r.data.telegram;
            if (pushTelegram && tg && !tg.ok) {
                flash(`Annuncio pubblicato, ma Telegram non inviato (${tg.reason}). Verifica la configurazione Telegram.`);
            } else {
                flash("Annuncio pubblicato.");
            }
            setTitle(""); setBody(""); setPinned(false);
            await load();
        } catch (e) {
            setError(formatApiErrorDetail(e.response?.data?.detail));
        } finally { setSaving(false); }
    };

    const remove = async (id) => {
        if (!confirm("Eliminare questo annuncio?")) return;
        try {
            await api.delete(`/admin/announcements/${id}`);
            await load();
        } catch (e) { alert(formatApiErrorDetail(e.response?.data?.detail)); }
    };

    return (
        <div data-testid="admin-announcements">
            <div className="op-label text-tff7a1a">// ANNUNCI UFFICIALI</div>
            <h2 className="op-heading text-3xl mt-2">Annunci</h2>
            <p className="text-t8f95a3 text-sm mt-2 max-w-2xl">
                Gli annunci appaiono in <span className="text-tffffff">homepage</span> e (se attivo) vengono inviati al Topic
                <span className="text-tffffff"> 📢 Annunci Ufficiali</span> su Telegram.
            </p>

            {/* Composer */}
            <div className="op-card mt-6 p-5" style={{ borderTop: "3px solid rgb(var(--tc-ff7a1a))" }}>
                <label className="op-label text-[10px] text-t8f95a3">TITOLO</label>
                <input value={title} onChange={(e) => setTitle(e.target.value)}
                    placeholder="es. Apertura mercato estivo"
                    className="w-full mt-1 mb-3 bg-t0e1014 border border-t22252a text-tffffff text-sm px-3 py-2"
                    data-testid="ann-title" />

                <label className="op-label text-[10px] text-t8f95a3">TESTO</label>
                <textarea value={body} onChange={(e) => setBody(e.target.value)} rows={4}
                    placeholder="Scrivi qui il comunicato…"
                    className="w-full mt-1 mb-3 bg-t0e1014 border border-t22252a text-tffffff text-sm px-3 py-2"
                    data-testid="ann-body" />

                <div className="flex items-center gap-5 flex-wrap">
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={pinned} onChange={(e) => setPinned(e.target.checked)}
                            className="accent-tff7a1a w-4 h-4" data-testid="ann-pinned" />
                        <span className="text-sm text-tc9ccd3">📌 In evidenza</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={pushTelegram} onChange={(e) => setPushTelegram(e.target.checked)}
                            className="accent-t2bb6ff w-4 h-4" data-testid="ann-telegram" />
                        <span className="text-sm text-tc9ccd3">Invia su Telegram</span>
                    </label>
                    <label className="flex items-center gap-2 cursor-pointer">
                        <input type="checkbox" checked={pushWeb} onChange={(e) => setPushWeb(e.target.checked)}
                            className="accent-t2bb6ff w-4 h-4" data-testid="ann-web" />
                        <span className="text-sm text-tc9ccd3">Notifica push web</span>
                    </label>
                </div>

                <div className="mt-4 flex items-center gap-3">
                    <button onClick={publish} disabled={saving}
                        className="op-btn op-btn-primary text-xs" data-testid="ann-publish-btn">
                        {saving ? "Pubblicazione…" : "Pubblica annuncio"}
                    </button>
                    {msg && <span className="text-t7ce38b text-sm" data-testid="ann-msg">{msg}</span>}
                </div>
                {error && <div className="text-tff4d4d text-sm mt-3" data-testid="ann-error">{error}</div>}
            </div>

            {/* List */}
            <div className="mt-8">
                {loading ? (
                    <div className="op-label text-t8f95a3 animate-pulse">// CARICAMENTO…</div>
                ) : items.length === 0 ? (
                    <div className="op-card p-8 text-center text-t8f95a3 text-sm">Nessun annuncio.</div>
                ) : (
                    <div className="space-y-3">
                        {items.map((a) => (
                            <div key={a.id} className="op-card p-4" data-testid={`ann-row-${a.id}`}>
                                <div className="flex items-start justify-between gap-3">
                                    <div>
                                        <div className="text-tffffff font-bold">
                                            {a.pinned && <span className="text-tff7a1a mr-1">📌</span>}{a.title}
                                        </div>
                                        <div className="text-tc9ccd3 text-sm mt-1 whitespace-pre-wrap">{a.body}</div>
                                        <div className="op-mono text-[10px] text-t5c6370 mt-2">
                                            {a.author_name} · {(a.created_at || "").slice(0, 16).replace("T", " ")}
                                        </div>
                                    </div>
                                    <button onClick={() => remove(a.id)}
                                        className="op-btn op-btn-ghost text-[10px]" data-testid={`ann-delete-${a.id}`}>
                                        Elimina
                                    </button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        </div>
    );
}
